import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion'; // We still need this for the hero
import './Home.css';
import { FaUsers, FaShieldAlt, FaRocket } from 'react-icons/fa';
import AnimatedOnScroll from '../components/AnimatedOnScroll'; // <-- NEW IMPORT
import RotatingWord from '../components/RotatingWord'; // <-- NEW IMPORT

// Animation variants for hero (animates on page load)
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.3 }
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const Home = () => {

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0 }}
    >
      {/* 1. Hero Section (Unchanged, animates on load) */}
      <motion.section
        className="container hero-section"
        variants={containerVariants}
      >
        <motion.h1 variants={itemVariants} className="hero-title">
          Building <span className="highlight-primary">Future-Proof</span> Software
        </motion.h1>
        <motion.h2 variants={itemVariants} className="hero-subtitle">
          Apexio Labs: Where Innovation Meets{' '}
          <RotatingWord
            words={[
              'Excellence',
              'Innovation',
              'Perfection',
              'Quality',
              'Precision',
              'Mastery',
              'Expertise',
              'Brilliance',
              'Craftsmanship',
              'Technology',
              'Solutions',
              'Performance',
              'Scalability',
              'Security',
              'Efficiency'
            ]}
            interval={2500}
          />
          .
        </motion.h2>
        <motion.p variants={itemVariants} className="hero-description">
          We are a full-stack software development agency specializing in custom
          web applications, mobile solutions, and AI-driven data analytics.
        </motion.p>
        <motion.div variants={itemVariants} className="hero-cta">
          <Link to="/services" className="btn" data-hover>Our Services</Link>
          <Link to="/contact" className="btn btn-secondary" data-hover>Get in Touch</Link>
        </motion.div>
      </motion.section>

      {/* 2. Services Overview Section (Refactored) */}
      <section className="container home-section">
        <AnimatedOnScroll>
          <h2 className="section-title">Explore Our Expertise</h2>
        </AnimatedOnScroll>
        <AnimatedOnScroll delay={0.1}>
          <p className="section-subtitle">We offer a wide range of services to bring your vision to life.</p>
        </AnimatedOnScroll>

        <div className="services-overview-grid">
          <AnimatedOnScroll className="service-preview-card" data-hover>
            <h3>Web Development</h3>
            <p>Scalable and secure web apps using Python, Django, and React.</p>
            <Link to="/services" className="card-link">Learn More</Link>
          </AnimatedOnScroll>

          <AnimatedOnScroll className="service-preview-card" delay={0.1} data-hover>
            <h3>AI & Data Science</h3>
            <p>Unlock insights and automate processes with our AI/ML solutions.</p>
            <Link to="/services" className="card-link">Learn More</Link>
          </AnimatedOnScroll>

          <AnimatedOnScroll className="service-preview-card" delay={0.2} data-hover>
            <h3>Cloud & DevOps</h3>
            <p>Streamline your infrastructure for maximum efficiency and uptime.</p>
            <Link to="/services" className="card-link">Learn More</Link>
          </AnimatedOnScroll>
        </div>
      </section>

      {/* 3. Why Choose Us Section (Refactored) */}
      <section className="container home-section why-us-section">
        <AnimatedOnScroll>
          <h2 className="section-title">Why Partner with Apexio?</h2>
        </AnimatedOnScroll>
        <AnimatedOnScroll delay={0.1}>
          <p className="section-subtitle">We're not just coders; we're partners in your success.</p>
        </AnimatedOnScroll>

        <div className="why-us-grid">
          <AnimatedOnScroll className="why-us-card">
            <FaRocket className="why-us-icon" />
            <h3>Innovative Solutions</h3>
            <p>We leverage cutting-edge technology to create solutions that are not just functional, but groundbreaking.</p>
          </AnimatedOnScroll>
          <AnimatedOnScroll className="why-us-card" delay={0.1}>
            <FaShieldAlt className="why-us-icon" />
            <h3>Unmatched Security</h3>
            <p>Security isn't an afterthought. We build secure-by-design applications to protect your data and users.</p>
          </AnimatedOnScroll>
          <AnimatedOnScroll className="why-us-card" delay={0.2}>
            <FaUsers className="why-us-icon" />
            <h3>Collaborative Team</h3>
            <p>We work with you, not for you. Our process is transparent, iterative, and always values your input.</p>
          </AnimatedOnScroll>
        </div>
      </section>

      {/* 4. Our Process Section (Refactored) */}
      <section className="container home-section">
        <AnimatedOnScroll>
          <h2 className="section-title">Our Proven Process</h2>
        </AnimatedOnScroll>
        <AnimatedOnScroll delay={0.1}>
          <p className="section-subtitle">A streamlined workflow to ensure quality and efficiency.</p>
        </AnimatedOnScroll>

        <div className="process-timeline">
          <AnimatedOnScroll className="process-step">
            <div className="process-step-number">1</div>
            <div className="process-step-content">
              <h3>Discover & Define</h3>
              <p>We start by deeply understanding your goals, challenges, and target audience to map out a clear project scope.</p>
            </div>
          </AnimatedOnScroll>
          <AnimatedOnScroll className="process-step" delay={0.1}>
            <div className="process-step-number">2</div>
            <div className="process-step-content">
              <h3>Design & Prototype</h3>
              <p>Our UI/UX team creates intuitive wireframes and high-fidelity designs, ensuring a seamless user experience.</p>
            </div>
          </AnimatedOnScroll>
          <AnimatedOnScroll className="process-step" delay={0.2}>
            <div className="process-step-number">3</div>
            <div className="process-step-content">
              <h3>Develop & Test</h3>
              <p>Our engineers bring the designs to life with clean code, following agile methodologies and rigorous testing.</p>
            </div>
          </AnimatedOnScroll>
          <AnimatedOnScroll className="process-step" delay={0.3}>
            <div className="process-step-number">4</div>
            <div className="process-step-content">
              <h3>Deploy & Scale</h3>
              <p>We handle the launch, monitor performance, and provide ongoing support to help you scale your application.</p>
            </div>
          </AnimatedOnScroll>
        </div>
      </section>

      {/* 5. Call to Action (CTA) Section (Refactored) */}
      <section className="cta-section">
        <AnimatedOnScroll className="container cta-container">
          <h2>Let's Build Your Next Big Idea</h2>
          <p>Have a project in mind? We're ready to listen.</p>
          <Link to="/contact" className="btn" data-hover>Get a Free Quote</Link>
        </AnimatedOnScroll>
      </section>
    </motion.div>
  );
};

export default Home;